import {useHistory, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useEffect, useRef, useState} from "react";
import {Alert, Button, Card, Modal, Select, Space, Spin, Tooltip, Typography} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";
import ApiService from "../../core/service/api-service";
import ReportCreateEditForm from "../../app/report/ReportCreateEditForm";
import StudyInfo from "../../app/study/StudyInfo";
import StudyRequestGrid, {RequestGridLegend} from "../../app/request/request-data-grid/StudyRequestGrid";
import {DataGridProvider} from "../../core/components/data-grid/DataGridContext";
import { Scrollbars } from 'react-custom-scrollbars';
import {v4} from "uuid";
import {PERMISSIONS, useUser} from "../../core/context/UserContext";
import VoiceRecordGrid from "../../app/voice-record/voice-record-grid/VoiceRecordGrid";
import {useApp} from "../../core/context/AppContext";
import {useModal} from "../../core/context/ModalContext";
import {useSignalR} from "../../core/context/SignalRContext";
import UrlHelper from "../../core/utils/url-helper";
import './report-modal.css'

// onRefreshGrid: ekranı açan listenin tazelemesi. Rapor başka raportöre atanmışsa uyarıdaki "Yenile" bunu çağırır.
export default function ReportModal({studyId, requestId, onClose, onRefreshGrid})
{
    const {t} = useTranslation()
    const selectedRequestId = requestId

    const [request, setRequest] = useState({})
    const {user, hasPermission} = useUser()
    const [anamnez, setAnamnez] = useState(null)

    const [updateKey, setUpdateKey] = useState('')
    // Modal başlığının sağındaki alan: rapor formu istem bilgisini ve otomatik kaydet anahtarını buraya portal eder.
    const [headerSlot, setHeaderSlot] = useState(null)
    const {setModalTitle, closeModal} = useModal()
    const {onStudyAssignmentChanged} = useSignalR()

    // RAPORTÖR ATAMASI
    // Karar ekran açılırken burada verilir: raportör boşsa açana atanır, başkasına atanmışsa ekran
    // açılmaz ve kullanıcı uyarılır. Bu ekranı açan her yer (çekim/rapor/istem gridleri, kısayollar)
    // böylece aynı kurala tabi. Kilit sunucuda (study_user) durur; SignalR kopsa da geçerlidir.
    // Rapor yazma yetkisi olmayan kullanıcı zaten kaydedemez: atama yapılmaz, ekran doğrudan açılır.
    const canEdit = hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT)
    const [claimed, setClaimed] = useState(!canEdit)
    // Ekran açıkken atama başkasına verildiyse onun adı. Kaydetmeler sunucuda reddedilir; uyarı bunu önceden söyler.
    const [reassignedTo, setReassignedTo] = useState(null)
    // Kilit ayarı kapalı kullanıcı (admin vb.) kurala tabi değil: kaydetmeleri reddedilmez, uyarı da gösterilmez.
    const [lockEnabled, setLockEnabled] = useState(false)
    // Ekran açılmadan önce yayınlanmış (context'te kalmış) atama olayı bu ekranı ilgilendirmez.
    const initialAssignmentKey = useRef(onStudyAssignmentChanged?.key)

    useEffect(() => {
        return () => {
            onClose && onClose()
        }
    }, []);

    useEffect(() => {
        if (!canEdit)
            return

        let cancelled = false

        const claim = async () => {
            const response = await ApiService.StudyUser.ClaimReporter(studyId)
            if (cancelled)
                return

            if (response.success && response.data?.granted) {
                setLockEnabled(response.data.lockEnabled === true)
                setClaimed(true)
                return
            }

            // Açan listenin kapanış tazelemesi çalışmaz: liste kendiliğinden yenilenmez, kullanıcı uyarıdaki "Yenile" ile yeniler.
            closeModal({ skipOnClose: true })

            // İstek başarısızsa hata mesajını api-service zaten gösterdi.
            if (response.success) {
                Modal.warning({
                    title: t('Report Is Locked'),
                    content: t('REPORT_ASSIGNED_TO_OTHER_REPORTER', { name: response.data?.reporterName || '-' }),
                    ...(onRefreshGrid && {
                        okCancel: true,
                        okText: t('Refresh'),
                        cancelText: t('Close'),
                        onOk: () => onRefreshGrid(),
                    }),
                })
            }
        }

        claim()

        return () => {
            cancelled = true
        }
    }, [studyId]);

    useEffect(() => {
        const payload = onStudyAssignmentChanged?.payload
        if (!canEdit || !lockEnabled || !payload || onStudyAssignmentChanged.key === initialAssignmentKey.current)
            return

        if (payload.type !== 'REPORTER' || !payload.studyIds?.some(id => String(id) === String(studyId)))
            return

        const toAnotherUser = payload.reporterUserId && payload.reporterUserId !== user?.data?.id
        setReassignedTo(toAnotherUser ? (payload.reporterName || '-') : null)
    }, [onStudyAssignmentChanged]);

    // Hasta/çekim bilgisini modal başlığına taşı (gövdede yer kazanmak için); anamnez gövdede kalır.
    // Başlığın sağı rapor formunun (istem bilgisi + otomatik kaydet) — bkz. ReportCreateEditForm headerSlot.
    useEffect(() => {
        if (studyId && claimed)
            setModalTitle(
                <div className="rm-header">
                    <StudyInfo studyId={studyId} variant="header" />
                    <div className="rm-header-extra" ref={setHeaderSlot} />
                </div>
            )
    }, [studyId, claimed]);

    if (!claimed) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                <Spin />
            </div>
        )
    }

    // Eskiden "Bu çekim / Diğer" seçicisi vardı; artık tek liste: seçili çekim + aynı hasta/modalitede
    // StudyDateTime'ı ±1 saat içindeki çekimlerin istem ve ses kayıtları (related).
    const renderRequestGrid = () => (
        <DataGridProvider>
            <StudyRequestGrid
                hideReqRepPage={true}
                hideLegend={true}
                related={true}
                selectedRequestId={selectedRequestId}
                updateKey={updateKey}
                studyId={studyId}
                name={'request-report-table'}
                onSelected={(req, type) => {
                    setRequest({
                        ...req
                    })
                }}
            />
        </DataGridProvider>
    )

    return (
        <div className="report-modal">
            {
                reassignedTo && (
                    <Alert
                        type={'error'}
                        showIcon={true}
                        style={{ marginBottom: 12 }}
                        message={t('REPORT_REASSIGNED_WHILE_OPEN', { name: reassignedTo })}
                    />
                )
            }
            <div className="report-modal-grid">
                <div className="report-modal-col">
                    <section className="rm-card">
                        <div className="rm-card-head">{t('Clinical Information')}</div>
                        <div className="rm-card-body">
                            <StudyInfo
                                studyId={studyId}
                                variant="anamnez"
                                onChangeAnamnez={(data) => setAnamnez(data)}
                            />
                        </div>
                    </section>

                    {
                        hasPermission(PERMISSIONS.VOICE_RECORD_LIST) && (
                            <section className="rm-card">
                                <div className="rm-card-head">
                                    <span className="rm-card-title">{t('Voice Records')}</span>
                                </div>
                                <div className="rm-card-body">
                                    <DataGridProvider>
                                        <VoiceRecordGrid studyId={studyId} related={true}/>
                                    </DataGridProvider>
                                </div>
                            </section>
                        )
                    }

                    <section className="rm-card">
                        <div className="rm-card-head">
                            <span className="rm-card-title">
                                {t('Requests')}
                                <Tooltip title={<RequestGridLegend vertical={true} />}>
                                    <InfoCircleOutlined style={{ color: 'var(--text-3)', cursor: 'help' }} />
                                </Tooltip>
                            </span>
                        </div>
                        <div className="rm-card-body">
                            {renderRequestGrid()}
                        </div>
                    </section>
                </div>

                <section className="rm-card">
                    <ReportCreateEditForm
                        studyId={request.studyId}
                        request={request}
                        headerSlot={headerSlot}
                        onStudyRead={() => setUpdateKey(v4())}
                        onRequestRestored={() => setUpdateKey(v4())}
                        onSaved={async () => {
                            setUpdateKey(v4())
                            if(anamnez) {
                                const res = await ApiService.StudyAnamnez.EditClinicalInformation(request.studyId, anamnez)
                            }
                        }}
                    />
                </section>
            </div>
        </div>
    )
}
